import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { Sparkles, Send } from 'lucide-react';
import { Drawer, Spinner, useToast } from '@components/base/feedback';
import { Icon, Text } from '@components/base/display';
import { Button } from '@components/base/actions';
import { HStack, VStack } from '@components/base/layout';
import { streamChat, type ChatMessage } from '../api/aiChatService';
import { useAiChatStore } from './aiChatStore';
import { Markdown } from './Markdown';

interface AIChatProps {
  ses: string;
  idSin: string;
}

// User turns stay plain text (the bubble wrapper already applies
// `whitespace-pre-wrap`); assistant turns render as markdown so headings,
// lists, code, tables, etc. format correctly — including while streaming.
function renderBubbleBody(
  isStreamingPlaceholder: boolean,
  isUser: boolean,
  content: string,
) {
  if (isStreamingPlaceholder) {
    return (
      <span
        className="inline-flex items-center"
        data-testid="ai-typing-indicator"
      >
        <Spinner size="xs" />
      </span>
    );
  }
  if (isUser) {
    return content;
  }
  return <Markdown>{content}</Markdown>;
}

export function AIChat({ ses, idSin }: Readonly<AIChatProps>) {
  const open = useAiChatStore((s) => s.open);
  const setOpen = useAiChatStore((s) => s.setOpen);
  const messages = useAiChatStore((s) => s.messages);
  const isStreaming = useAiChatStore((s) => s.isStreaming);
  const appendUserMessage = useAiChatStore((s) => s.appendUserMessage);
  const startAssistantMessage = useAiChatStore((s) => s.startAssistantMessage);
  const appendAssistantDelta = useAiChatStore((s) => s.appendAssistantDelta);
  const setAbortController = useAiChatStore((s) => s.setAbortController);
  const abortStream = useAiChatStore((s) => s.abortStream);

  const { toast } = useToast();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState('');

  // Scroll to the newest message whenever the thread grows, a delta streams
  // in, or the panel opens. requestAnimationFrame defers the scroll until the
  // store-driven render commits — without it the ref points at the stale DOM.
  const lastMessage = messages[messages.length - 1];
  const lastContent = lastMessage?.content ?? '';
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ block: 'end' });
    });
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [open, messages.length, lastContent]);

  // Abort any in-flight stream when the panel closes or unmounts. The
  // controller lives in the store (see `aiChatStore.ts`) so these paths and
  // the page-level `reset()` on a claim change all cancel the same request.
  useEffect(() => {
    if (!open) {
      abortStream();
    }
  }, [open, abortStream]);

  useEffect(() => {
    return () => {
      useAiChatStore.getState().abortStream();
    };
  }, []);

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;

    // Snapshot the prior thread before mutating the store so the request
    // carries the full history plus the just-typed user turn.
    const history = useAiChatStore.getState().messages;
    const outbound: ChatMessage[] = [
      ...history.map((m) => ({ role: m.role, content: m.content })),
      { role: 'user', content: trimmed },
    ];

    appendUserMessage(trimmed);
    setText('');
    startAssistantMessage();

    const controller = new AbortController();
    setAbortController(controller);

    try {
      await streamChat({
        ses,
        idSin,
        messages: outbound,
        // A read that already resolved before the abort still lands here on a
        // later microtask, so gate every delta on the signal: once the claim
        // changed (or the panel closed) this stream must not touch the store.
        onDelta: (delta) => {
          if (controller.signal.aborted) return;
          appendAssistantDelta(delta);
        },
        signal: controller.signal,
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = err instanceof Error ? err.message : 'Erreur inconnue.';
      toast({
        variant: 'error',
        title: 'Échec de la requête IA',
        message,
      });
      // Surface the failure inside the (otherwise empty) assistant bubble.
      appendAssistantDelta(`⚠️ ${message}`);
    } finally {
      // Only settle the store if this request is still the current one. After
      // a `reset()` / `abortStream()` the controller has been replaced (or
      // cleared) and the streaming flag already belongs to the new thread.
      const state = useAiChatStore.getState();
      if (state.abortController === controller) {
        state.setAbortController(null);
        state.finishAssistant();
      }
    }
  }, [
    text,
    isStreaming,
    ses,
    idSin,
    appendUserMessage,
    startAssistantMessage,
    appendAssistantDelta,
    setAbortController,
    toast,
  ]);

  const handleTextChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend],
  );

  const canSend = text.trim().length > 0 && !isStreaming;
  const showTyping =
    isStreaming &&
    lastMessage?.role === 'assistant' &&
    lastMessage.content.length === 0;

  return (
    <Drawer
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      side="right"
      width="md"
    >
      <Drawer.Header>
        <HStack gap={3} align="center">
          <Icon icon={Sparkles} size="sm" color="primary" />
          <Text as="span" weight="semibold" size="base">
            Assistant IA
          </Text>
        </HStack>
      </Drawer.Header>
      <Drawer.Body>
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Text size="sm" color="muted">
              Posez une question sur ce dossier.
            </Text>
          </div>
        )}
        {messages.length > 0 && (
          <VStack gap={3}>
            {messages.map((m, idx) => {
              const isUser = m.role === 'user';
              const isLastAssistantStreaming =
                showTyping && idx === messages.length - 1;
              return (
                <div
                  key={`${m.role}-${String(idx)}`}
                  data-testid={`ai-message-${m.role}`}
                  className={[
                    'flex',
                    isUser ? 'justify-end' : 'justify-start',
                  ].join(' ')}
                >
                  <div
                    className={[
                      'max-w-[85%] rounded-lg px-3 py-2 text-sm break-words',
                      isUser
                        ? 'whitespace-pre-wrap bg-primary-100 text-primary-900'
                        : 'bg-secondary-50 text-secondary-800',
                    ].join(' ')}
                  >
                    {renderBubbleBody(isLastAssistantStreaming, isUser, m.content)}
                  </div>
                </div>
              );
            })}
          </VStack>
        )}
        <div ref={bottomRef} data-testid="ai-chat-bottom" aria-hidden="true" />
      </Drawer.Body>
      <Drawer.Footer className="flex-col items-stretch gap-2 !justify-start">
        <div className="flex items-end gap-2 w-full">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder="Écrire un message..."
            rows={2}
            className="flex-1 resize-none rounded-md border border-secondary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-500"
            aria-label="Écrire un message"
          />
          <Button
            variant="primary"
            onClick={() => void handleSend()}
            disabled={!canSend}
            loading={isStreaming}
            leftIcon={<Send className="w-4 h-4" />}
            aria-label="Envoyer le message"
          />
        </div>
      </Drawer.Footer>
    </Drawer>
  );
}
